import { useEffect, useState } from "react";
import {
  User,
  Calendar,
  Clock,
  Timer,
  ListChecks,
  MessageSquare,
  Save,
  RotateCcw,
  CheckCircle2,
  XCircle,
  Plane,
  Sparkles,
  Sun,
  Moon,
} from "lucide-react";
import { createAttendance, fetchAttendance, updateAttendance } from "./attendanceApi";
import { fetchWfHolidays } from "./wfApi";
import { fetchCompOffSettings } from "../leave/compOffApi";
import API from "../../services/api";
import CompOffGrantDialog from "../../components/attendance/CompOffGrantDialog";
import {
  compOffGrantMessage,
  compOffGrantOffer,
  creditDayLabel,
} from "../../components/attendance/compOffGrantPrompt";
import {
  fixedWorkingDaysFromPayroll,
  isCalendarWeekOffDay,
  monthlyOffConfigFromAttendance,
  parseWorkDate,
  weekOffWeekdaysFromAttendance,
} from "./attendanceCalendarUtils";
import EmployeeSelect from "../../components/common/EmployeeSelect";
import SuccessScreen from "../../components/common/SuccessScreen";
import OrganisationDisplayField from "../../components/common/OrganisationDisplayField";
import DatePickerField from "../../components/common/DatePickerField";
import TimePickerField from "../../components/common/TimePickerField";
import {
  calcWorkHoursFromParts,
  combineDateAndTime,
} from "../../components/common/datePickerUtils";
import { formatIndianDate } from "./indianCalendarUtils";

/** Any fixed day: work hours depend only on the two times (checkout before check-in = next day). */
const HOURS_REFERENCE_DATE = "2000-01-03";

const STATUS_OPTIONS = [
  { value: "P", label: "Present", icon: CheckCircle2, gradient: "from-emerald-500 to-green-600", soft: "from-emerald-50 to-green-50", ring: "ring-emerald-300", text: "text-emerald-700", border: "border-emerald-200" },
  { value: "A", label: "Absent (LOP)", icon: XCircle, gradient: "from-rose-500 to-red-600", soft: "from-rose-50 to-red-50", ring: "ring-rose-300", text: "text-rose-700", border: "border-rose-200" },
  { value: "HD", label: "Half day", icon: Sun, gradient: "from-amber-500 to-orange-600", soft: "from-amber-50 to-orange-50", ring: "ring-amber-300", text: "text-amber-700", border: "border-amber-200" },
  { value: "L", label: "Leave", icon: Plane, gradient: "from-sky-500 to-blue-600", soft: "from-sky-50 to-blue-50", ring: "ring-sky-300", text: "text-sky-700", border: "border-sky-200" },
  { value: "WO", label: "Week off", icon: Moon, gradient: "from-slate-500 to-slate-700", soft: "from-slate-50 to-slate-100", ring: "ring-slate-300", text: "text-slate-700", border: "border-slate-200" },
  { value: "H", label: "Holiday", icon: Sparkles, gradient: "from-violet-500 to-fuchsia-600", soft: "from-violet-50 to-fuchsia-50", ring: "ring-violet-300", text: "text-violet-700", border: "border-violet-200" },
];

const blankForm = (orgId) => ({
  organisation_id: orgId || "",
  employee_id: "",
  attendance_date: "",
  check_in_time: "",
  check_out_time: "",
  total_hours: "",
  status: "P",
  remarks: "",
});

function SectionLabel({ icon: Icon, color, children }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${color} text-white flex items-center justify-center shadow-sm`}>
        <Icon size={12} />
      </div>
      <span className="text-sm font-semibold text-slate-800">{children}</span>
    </div>
  );
}

export default function AttendanceForm({
  reload,
  defaultOrganisationId = "",
  defaultOrganisationName = "",
  hideOrganisationId = false,
  externalEmployeeId = "",
  externalAttendanceDate = "",
  hideEmployeeField = false,
  hideDateField = false,
  onSaveSuccess,
  inline = false,
  employeeName = "this employee",
}) {
  const [form, setForm] = useState(blankForm(defaultOrganisationId));
  const [saving, setSaving] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [hoursAuto, setHoursAuto] = useState(false);
  const [compOffSettings, setCompOffSettings] = useState(null);
  const [weekOffWeekdays, setWeekOffWeekdays] = useState(null);
  const [monthlyOff, setMonthlyOff] = useState(null);
  const [fixedWorkingDays, setFixedWorkingDays] = useState(null);
  const [holiday, setHoliday] = useState(null);
  const [compOffAsk, setCompOffAsk] = useState(null);

  useEffect(() => {
    if (defaultOrganisationId) {
      setForm((prev) => ({ ...prev, organisation_id: defaultOrganisationId }));
    }
  }, [defaultOrganisationId]);

  useEffect(() => {
    if (hideEmployeeField && externalEmployeeId) {
      setForm((prev) => (prev.employee_id === externalEmployeeId ? prev : { ...prev, employee_id: externalEmployeeId }));
    }
  }, [externalEmployeeId, hideEmployeeField]);

  useEffect(() => {
    if (hideDateField && externalAttendanceDate) {
      setForm((prev) => (prev.attendance_date === externalAttendanceDate ? prev : { ...prev, attendance_date: externalAttendanceDate }));
    }
  }, [externalAttendanceDate, hideDateField]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [settings, hr] = await Promise.all([
          fetchCompOffSettings(),
          API.get("/organisation/me/hr-settings"),
        ]);
        if (cancelled) return;
        setCompOffSettings(settings);
        const att = hr.data?.attendance_settings || hr.data?.hr_settings?.attendance || {};
        setWeekOffWeekdays(weekOffWeekdaysFromAttendance(att));
        setMonthlyOff(monthlyOffConfigFromAttendance(att));
        setFixedWorkingDays(fixedWorkingDaysFromPayroll(hr.data?.payroll_settings || {}));
      } catch {
        if (!cancelled) setCompOffSettings(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!form.attendance_date) {
      setHoliday(null);
      return undefined;
    }
    let cancelled = false;
    fetchWfHolidays({ from_date: form.attendance_date, to_date: form.attendance_date })
      .then((rows) => {
        if (cancelled) return;
        const match = (rows || []).find(
          (row) => String(row.holiday_date || "").slice(0, 10) === form.attendance_date
        );
        setHoliday(match || null);
      })
      .catch(() => {
        if (!cancelled) setHoliday(null);
      });
    return () => {
      cancelled = true;
    };
  }, [form.attendance_date]);

  useEffect(() => {
    // Hours come only from the entered check-in / check-out times (no work date or clock needed).
    const hrs = calcWorkHoursFromParts(
      form.attendance_date || HOURS_REFERENCE_DATE,
      form.check_in_time,
      form.check_out_time
    );

    if (hrs == null) {
      setHoursAuto(false);
      if (!form.check_in_time || !form.check_out_time) {
        setForm((prev) => (prev.total_hours === "" ? prev : { ...prev, total_hours: "" }));
      }
      return;
    }

    setHoursAuto(true);
    const next = String(hrs);
    setForm((prev) => (prev.total_hours === next ? prev : { ...prev, total_hours: next }));
  }, [form.attendance_date, form.check_in_time, form.check_out_time]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "total_hours") setHoursAuto(false);
    setForm({ ...form, [name]: value });
    setMessage({ type: "", text: "" });
  };

  const resetForm = () => {
    const base = blankForm(defaultOrganisationId);
    if (hideEmployeeField && externalEmployeeId) base.employee_id = externalEmployeeId;
    if (hideDateField && externalAttendanceDate) base.attendance_date = externalAttendanceDate;
    setForm(base);
    setHoursAuto(false);
    setMessage({ type: "", text: "" });
  };

  const offerForCurrentDay = () => {
    if (form.status !== "P" && form.status !== "HD") return null;
    const parts = parseWorkDate(form.attendance_date);
    if (!parts) return null;
    const weekOff = isCalendarWeekOffDay(parts.day, parts.weekdayIndexMon0, parts.monthKey, {
      weekOffWeekdays,
      monthlyOff,
      fixedWorkingDays,
    });
    const offer = compOffGrantOffer({
      enabled: Boolean(compOffSettings?.enabled),
      applicableDays: compOffSettings?.applicable_days || "both",
      isHoliday: Boolean(holiday),
      isWeekOff: weekOff,
    });
    if (!offer) return null;
    return {
      ...offer,
      holidayName: holiday?.name || "",
      dateLabel: formatIndianDate(form.attendance_date, { withWeekday: true }),
      employeeName: employeeName || "this employee",
      creditLabel: creditDayLabel(compOffSettings?.credit_value),
    };
  };

  const handleSubmit = async (e, compOffDecision) => {
    if (e?.preventDefault) e.preventDefault();

    if (!form.employee_id || !form.attendance_date) {
      setMessage({ type: "err", text: "Please select an employee and work date from the filters above, then click Apply." });
      return;
    }

    if (!compOffDecision) {
      const offer = offerForCurrentDay();
      if (offer) {
        setCompOffAsk(offer);
        return;
      }
    }
    setCompOffAsk(null);

    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const derivedHours = calcWorkHoursFromParts(
        form.attendance_date || HOURS_REFERENCE_DATE,
        form.check_in_time,
        form.check_out_time
      );
      const workHours =
        form.total_hours !== "" && form.total_hours != null
          ? Number(form.total_hours || 0)
          : derivedHours ?? Number(form.total_hours || 0);

      const payload = {
        organisation_id: form.organisation_id,
        employee_id: form.employee_id,
        work_date: form.attendance_date,
        time_in: combineDateAndTime(form.attendance_date, form.check_in_time) || null,
        time_out: combineDateAndTime(form.attendance_date, form.check_out_time) || null,
        work_hours: workHours,
        status: form.status,
        remarks: form.remarks || null,
        ...(compOffDecision ? { comp_off_decision: compOffDecision } : {}),
      };

      const findExistingRow = async () => {
        const rows = await fetchAttendance({
          employee_id: payload.employee_id || undefined,
          for_date: payload.work_date || undefined,
        });
        if (!Array.isArray(rows) || rows.length === 0) return null;
        const exact = rows.find((r) => String(r.work_date) === String(payload.work_date));
        return exact || rows[0] || null;
      };

      const existingRow = await findExistingRow();
      let successMsg = "";
      let grant = null;
      const changes = {
        time_in: payload.time_in,
        time_out: payload.time_out,
        work_hours: payload.work_hours,
        status: payload.status,
        remarks: payload.remarks,
        ...(compOffDecision ? { comp_off_decision: compOffDecision } : {}),
      };
      if (existingRow?.attendance_id) {
        grant = await updateAttendance(existingRow.attendance_id, changes);
        successMsg = "Attendance updated for this date.";
      } else {
        try {
          grant = await createAttendance(payload);
          successMsg = "Attendance saved.";
        } catch (err) {
          if (String(err?.message || "").toLowerCase().includes("already exists")) {
            const retryRow = await findExistingRow();
            if (retryRow?.attendance_id) {
              grant = await updateAttendance(retryRow.attendance_id, changes);
              successMsg = "Attendance updated (existing row).";
            } else throw err;
          } else throw err;
        }
      }
      const grantNote = compOffGrantMessage(
        grant?.comp_off_grant,
        compOffDecision,
        creditDayLabel(compOffSettings?.credit_value)
      );
      if (grantNote) successMsg = `${successMsg} ${grantNote}`;

      setMessage({ type: "ok", text: successMsg });
      setForm(blankForm(defaultOrganisationId || form.organisation_id));
      setHoursAuto(false);
      reload?.();

      // SuccessScreen calls onSaveSuccess after its delay (timer cleared on unmount).
      if (onSaveSuccess) setRedirecting(true);
    } catch (err) {
      setMessage({ type: "err", text: err.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  const selectedStatus = STATUS_OPTIONS.find((s) => s.value === form.status) || STATUS_OPTIONS[0];

  return (
    <form onSubmit={handleSubmit} className={inline ? "relative" : "relative bg-white shadow-xl shadow-slate-200/60 border border-slate-200 rounded-3xl overflow-hidden"}>
      {compOffAsk ? (
        <CompOffGrantDialog
          dayKind={compOffAsk.dayKind}
          holidayName={compOffAsk.holidayName}
          dateLabel={compOffAsk.dateLabel}
          employeeName={compOffAsk.employeeName}
          creditLabel={compOffAsk.creditLabel}
          busy={saving}
          onAllow={() => handleSubmit(null, "allow")}
          onSkip={() => handleSubmit(null, "skip")}
          onCancel={() => { if (!saving) setCompOffAsk(null); }}
        />
      ) : null}
      {!inline && <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-indigo-500 via-sky-500 via-teal-500 to-emerald-500" />}

      {redirecting && (
        <SuccessScreen
          title="Attendance saved successfully!"
          message="Redirecting to attendance records…"
          onComplete={onSaveSuccess}
        />
      )}

      <div className={inline ? "" : "p-6 md:p-7"}>
        {message.text && (
          <div
            className={`mb-5 flex items-start gap-2 text-sm px-3 py-2.5 rounded-xl border-l-4 shadow-sm ${
              message.type === "ok"
                ? "bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-500 text-emerald-800"
                : "bg-gradient-to-r from-rose-50 to-red-50 border-rose-500 text-rose-800"
            }`}
          >
            {message.type === "ok" ? (
              <CheckCircle2 size={16} className="shrink-0 mt-0.5 text-emerald-600" />
            ) : (
              <XCircle size={16} className="shrink-0 mt-0.5 text-rose-600" />
            )}
            <span className="font-semibold">{message.text}</span>
          </div>
        )}

        <div className="space-y-9">
          {!hideOrganisationId && (
            <OrganisationDisplayField
              organisationId={form.organisation_id}
              organisationName={defaultOrganisationName}
              hint="Attendance is recorded for this organisation."
            />
          )}

          {!hideEmployeeField && (
            <div>
              <SectionLabel icon={User} color="from-indigo-500 to-purple-600">
                Employee <span className="text-rose-500">*</span>
              </SectionLabel>
              <EmployeeSelect
                hideLabel
                required
                name="employee_id"
                value={form.employee_id}
                onChange={(id) => {
                  setForm({ ...form, employee_id: id });
                  setMessage({ type: "", text: "" });
                }}
                selectClassName="border-2 border-slate-200 rounded-xl p-2.5 w-full text-sm focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none transition"
              />
            </div>
          )}

          {!hideDateField && (
            <div>
              <SectionLabel icon={Calendar} color="from-sky-500 to-blue-600">
                Work date <span className="text-rose-500">*</span>
              </SectionLabel>
              <DatePickerField
                hideLabel
                required
                value={form.attendance_date}
                onChange={(date) => {
                  setForm((prev) => ({ ...prev, attendance_date: date }));
                  setMessage({ type: "", text: "" });
                }}
                placeholder="Select work date"
              />
            </div>
          )}

          {/* TIMES + HOURS — one row */}
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <SectionLabel icon={Clock} color="from-emerald-500 to-teal-600">
                Check in
              </SectionLabel>
              <TimePickerField
                hideLabel
                value={form.check_in_time}
                onChange={(time) => {
                  setForm((prev) => ({ ...prev, check_in_time: time }));
                  setMessage({ type: "", text: "" });
                }}
                placeholder="Select check-in time"
              />
            </div>
            <div>
              <SectionLabel icon={Clock} color="from-emerald-500 to-teal-600">
                Check out
              </SectionLabel>
              <TimePickerField
                hideLabel
                value={form.check_out_time}
                onChange={(time) => {
                  setForm((prev) => ({ ...prev, check_out_time: time }));
                  setMessage({ type: "", text: "" });
                }}
                placeholder="Select check-out time"
              />
            </div>
            <div>
              <SectionLabel icon={Timer} color="from-amber-500 to-orange-600">
                Work hours {hoursAuto ? <span className="ml-1 text-xs font-normal text-emerald-600">· auto</span> : null}
              </SectionLabel>
              <input
                name="total_hours"
                type="number"
                min="0"
                step="0.01"
                placeholder="Auto-filled from check-in / check-out"
                value={form.total_hours}
                className={`border-2 p-2.5 w-full rounded-xl text-sm focus:ring-4 outline-none transition ${
                  hoursAuto
                    ? "border-emerald-300 bg-emerald-50/40 text-emerald-900 focus:border-emerald-500 focus:ring-emerald-100"
                    : "border-slate-200 focus:border-amber-500 focus:ring-amber-100"
                }`}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* STATUS — colorful pill picker — one row */}
          <div>
            <SectionLabel icon={ListChecks} color="from-violet-500 to-fuchsia-600">
              Status <span className="text-rose-500">*</span>
            </SectionLabel>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {STATUS_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const active = form.status === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setForm({ ...form, status: opt.value });
                      setMessage({ type: "", text: "" });
                    }}
                    className={`group relative flex items-center gap-1.5 px-2 py-1.5 rounded-lg border-2 text-xs font-semibold transition-all duration-200 ${
                      active
                        ? `bg-gradient-to-br ${opt.gradient} text-white border-transparent shadow-md ring-2 ${opt.ring}`
                        : `bg-gradient-to-br ${opt.soft} ${opt.text} ${opt.border} hover:shadow-sm`
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded flex items-center justify-center shrink-0 transition ${
                        active
                          ? "bg-white/25 text-white"
                          : `bg-gradient-to-br ${opt.gradient} text-white shadow-sm`
                      }`}
                    >
                      <Icon size={10} />
                    </div>
                    <span>{opt.label}</span>
                    <span className={`ml-auto text-[9px] font-bold px-1 py-0.5 rounded ${active ? "bg-white/25 text-white" : "bg-white text-slate-500 border border-slate-200"}`}>
                      {opt.value}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Present and half-day count toward payable days in payroll. Your selection is saved as entered
              (not replaced by the holiday calendar).
            </p>
          </div>

          {/* REMARKS — TEAL */}
          <div>
            <SectionLabel icon={MessageSquare} color="from-teal-500 to-cyan-600">
              Remarks <span className="text-slate-400 text-xs font-normal">(optional)</span>
            </SectionLabel>
            <textarea
              name="remarks"
              value={form.remarks}
              placeholder="Any context worth recording…"
              className="border-2 border-slate-200 p-2.5 w-full rounded-xl text-sm focus:border-teal-500 focus:ring-4 focus:ring-teal-100 outline-none transition resize-none"
              onChange={handleChange}
              rows={2}
            />
          </div>
        </div>

        {/* ACTIONS */}
        <div className="mt-7 pt-5 border-t border-slate-100 flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white shadow-md transition-all duration-200 ${
              saving
                ? "bg-slate-400 cursor-not-allowed"
                : `bg-gradient-to-r ${selectedStatus.gradient} hover:shadow-xl hover:-translate-y-0.5`
            }`}
          >
            <Save size={15} className={saving ? "animate-pulse" : ""} />
            {saving ? "Saving…" : `Save as ${selectedStatus.label}`}
          </button>

          <button
            type="button"
            onClick={resetForm}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold border-2 border-rose-200 text-rose-700 hover:bg-rose-50 hover:border-rose-300 transition disabled:opacity-50"
          >
            <RotateCcw size={14} />
            Reset
          </button>

          <span className="ml-auto text-xs text-slate-500 hidden sm:inline-flex items-center gap-1">
            <Sparkles size={11} className="text-amber-500" />
            Saved entry overwrites any existing record for the same date.
          </span>
        </div>
      </div>
    </form>
  );
}
